// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Enamel is glass, fused to metal in a kiln at somewhere around 800 degrees. That single fact explains almost everything about it: why the colours are so deep, why it survives a century without fading, and why a knock against a door frame can take a chip out of it that nobody can invisibly repair.

## The techniques worth knowing

**Champleve.** Recesses are carved or cast into the metal and filled with enamel, then ground flat. You see metal walls between the colours, and those walls are part of the design. Robust, and common on Victorian pieces.

**Cloisonne.** Thin metal wires are soldered onto the surface to make cells, and the cells are filled. Similar effect to champleve, but the dividing lines are wires standing on the surface rather than walls cut into it.

**Plique a jour.** Enamel held in an open framework with no metal behind it, so light passes through like a stained glass window. Fragile, technically brutal to make, and the signature of the finest [Art Nouveau](/blog/art-deco-vs-art-nouveau-jewelry) work. Hold it up to a window: it glows.

**Basse taille.** The metal underneath is engraved or chased with a pattern, then covered with translucent enamel, so the design shows through with varying depth of colour.

**Guilloche.** A machine engraved pattern of regular waves or rays under translucent enamel. The mechanical regularity of the engraving is the point, and it gives that shimmering, moire effect. Late nineteenth century onwards, and forever associated with Faberge.

**Grisaille.** Painted in white on a dark ground, building up shading in layers. Looks like a small monochrome painting.

**Painted or Limoges enamel.** A miniature actually painted in enamel colours and fired in stages. Portraits and scenes.

## How to tell enamel from paint or plastic

This matters, because painted and cold-enamel imitations are everywhere.

| Check | Real fired enamel | Paint, resin or cold enamel |
| --- | --- | --- |
| Temperature | Cool to the touch, like glass | Warms immediately |
| Tap with a fingernail | Hard, glassy click | Dull |
| Under a loupe | Tiny bubbles and pinholes from firing | Brush marks, or perfectly flat |
| Edges | Flush with the metal, ground and polished | Sits proud, may have a lip |
| Damage | Chips, with a sharp glassy edge | Scratches, peels or lifts |
| Solvent on a hidden spot | Unaffected | May soften or lift |

The bubbles are the giveaway. Fired enamel almost always shows a scatter of tiny voids under magnification, because it was liquid glass with air in it.

## Damage, and what can and cannot be fixed

**Chips.** The most common damage, usually at edges and high points. A chip in fired enamel cannot be invisibly repaired. It can be filled with a cold resin, which is a cosmetic fix, not a restoration, and it should be disclosed.

**Hairline cracks.** Often from a knock or from thermal shock. They tend to spread.

**Crazing.** A network of fine cracks across the surface, usually age or stress.

**Loss.** Areas where the enamel has gone entirely, leaving the metal recess visible. On champleve this reads as bare metal cells.

Refiring a damaged antique piece is theoretically possible and practically a bad idea: the heat that melts new enamel also melts solder joins, damages stones and can crack the rest of the enamel. Very few people will attempt it, and it is expensive when they do.

So the rule when buying: **price the enamel condition, do not plan to fix it.**

## Caring for enamel

- **Never use an ultrasonic cleaner.** Vibration and existing hairlines do not mix.
- **Never use steam or hot water.** Thermal shock cracks glass.
- **Wipe with a barely damp soft cloth**, and dry it.
- **Keep it away from other jewelry** in storage. Anything harder will chip it, and almost everything is harder.
- **Take it off** for anything involving hands and hard surfaces.

Our [cleaning guide](/blog/how-to-clean-antique-jewelry) covers the wider list of what must stay out of water and ultrasonics.

## Where enamel turns up, by period

- **Georgian and early Victorian**: black enamel on mourning pieces, often with an inscription and a date on the reverse.
- **Victorian**: champleve borders, Etruscan revival work, blue and black enamel accents on gold.
- **Art Nouveau**, roughly 1890 to 1910: plique a jour wings and petals, subtle greens and violets, enamel as the whole point of the piece.
- **Edwardian and Faberge era**: guilloche in pastel tones, often on cases and small objects as much as jewelry.
- **Art Deco**: flat black enamel as a graphic block against white metal and diamonds.
- **Mid century**: bright opaque enamels on costume and gold pieces alike.

Enamel is therefore a dating clue in its own right. A large area of translucent enamel over engine turning suggests Edwardian; a hard black rectangle beside a line of calibre cut sapphires suggests Deco. The [era by era guide](/blog/how-to-date-antique-jewelry-by-era) puts that in context.

## Buying enamel second hand

Ask for photographs taken in daylight from at least two angles, and one taken **backlit** if the piece has any translucent enamel. Backlighting shows both whether the enamel is genuinely translucent and whether there is a crack running through it.

Ask directly: are there any chips, losses or repairs, and where. Enamel damage is easy to hide with a careful camera angle, and easy to disclose honestly.

Ask whether any area has been filled with resin. On an older restoration this is common and is not dishonest, but you are paying a different price for a filled piece.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed, so enamel damage that was not in the description is something you can act on rather than absorb. [See what is listed](/s), or read [how payments work](/how-payments-work).

The Hallmark Society's [Art Nouveau guide](https://hallmarksociety.com/art-nouveau-jewelry/) goes further into plique a jour, and why so little of it survives.
`;

export default body;
